import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useState, useRef, useEffect } from 'react'

import { User } from '../models/Users'
import { useInvitations } from '@/utils/hooks/invitations'

interface OrgUserAddProps {
  invitingUser: boolean
  setInvitingUser: React.Dispatch<React.SetStateAction<boolean>>
  currentUser: User
  token: string
  orgUuid: string
}

export default function OrgUserAdd({
  invitingUser,
  setInvitingUser,
  currentUser,
  token,
  orgUuid,
}: OrgUserAddProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'user'>('admin')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [invitationLink, setInvitationLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [inviteMode, setInviteMode] = useState<string | null>(null)
  const [inviteTabValue, setInviteTabValue] = useState(0)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const alertRef = useRef<HTMLDivElement>(null)

  const { sendInvitation } = useInvitations(token, orgUuid)

  // focus the alert when it appears (success or error)
  useEffect(() => {
    if ((successMessage || error) && alertRef.current) {
      alertRef.current.focus()
    }
  }, [successMessage, error])

  function handleInviteModeToggle(inviteMode: string) {
    setInviteMode((prevType) => (prevType === inviteMode ? null : inviteMode))
    if (inviteMode === 'link') {
      setInviteTabValue(0)
    } else if (inviteMode === 'email') {
      setInviteTabValue(1)
    }
  }

  function handleInviteTabChange(
    event: React.SyntheticEvent,
    newValue: number,
  ) {
    setInviteTabValue(newValue)
    setEmail('') // reset email value when tab changes
    setSuccessMessage(null) // reset success message
    setError(null) // reset error
    setInviteMode(newValue === 0 ? 'link' : 'email')
    if (newValue === 0 && invitationLink) {
      setInvitationLink(null)
    }
  }

  const handleCopyLink = () => {
    if (invitationLink) {
      navigator.clipboard.writeText(invitationLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (email.toLowerCase() === currentUser.email.toLowerCase()) {
      setError('You cannot send an invitation to yourself')
      return
    }

    setIsLoading(true)

    try {
      const invitation = await sendInvitation.mutateAsync({
        email,
        orgUuid,
        inviterUuid: currentUser.uuid,
        role,
        token,
      })

      setInvitationLink(invitation.invitation_link)

      // set success message based on invite mode
      if (inviteMode === 'link') {
        setSuccessMessage(
          'Invitation link created successfully! Copy the link below and send it to the person you want to invite.',
        )
      } else if (inviteMode === 'email') {
        setSuccessMessage(`Invitation email sent successfully to ${email}`)
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(
          err.message.includes('already invited')
            ? 'User already invited'
            : err.message.includes('already part')
              ? 'User is already part of this organisation'
              : "Sorry, failed to send invitation. Please make sure the email is valid if you've added one.",
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      open={invitingUser}
      onClose={() => setInvitingUser(false)}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        Invite someone to your Organisation
      </DialogTitle>

      <form onSubmit={() => setInvitingUser(false)}>
        <DialogContent sx={{ width: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              textAlign: 'center',
              mb: 2,
            }}
          >
            Invitations are single-use and expire after 14 days.
            <br />
            If inviting by email, the invited user must be logged in with the
            same email address to accept the invitation.
          </Typography>

          <Divider sx={{ my: 2 }} />

          {inviteMode === null ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                gap: 2,
                mb: 1,
              }}
            >
              <Button
                disableRipple
                sx={{
                  mt: 2,
                  borderColor: '#1C3045',
                  bgcolor: '#fff',
                  color: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  '&:focus': {
                    border: '2px solid #1C3045',
                  },
                }}
                variant="outlined"
                onClick={() => handleInviteModeToggle('link')}
              >
                Invite via Link
              </Button>
              <Button
                disableRipple
                sx={{
                  mt: 2,
                  borderColor: '#1C3045',
                  bgcolor: '#fff',
                  color: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  '&:focus': {
                    border: '2px solid #1C3045',
                  },
                }}
                variant="outlined"
                onClick={() => handleInviteModeToggle('email')}
              >
                Invite via Email
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                mb: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Tabs
                value={inviteTabValue}
                onChange={handleInviteTabChange}
                sx={{
                  mt: 2,
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '16px',
                    minHeight: '48px',
                    borderRadius: '4px',
                    border: '1px solid #1C3045',
                    marginRight: '16px',
                    minWidth: '120px',
                    '&.Mui-selected': {
                      backgroundColor: '#1C3045',
                      color: '#fff',
                    },
                    '&:not(.Mui-selected)': {
                      backgroundColor: '#fff',
                      color: '#1C3045',
                    },

                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#1C3045',
                    },
                    '&:focus': {
                      border: '2px solid #1C3045',
                    },
                  },
                }}
              >
                <Tab disableRipple label="Invite via Link" />
                <Tab disableRipple label="Invite via Email" />
              </Tabs>
            </Box>
          )}
          {inviteMode === 'link' && (
            <Typography
              variant="body1"
              sx={{ mt: 2, mb: 5, textAlign: 'center' }}
            >
              Create a single-use sharable link to invite someone to your
              organisation.
            </Typography>
          )}
          {inviteMode === 'email' && (
            <>
              <Typography variant="body1" sx={{ my: 2, textAlign: 'center' }}>
                Enter the email address of the user you would like to invite to
                your organisation. <br />
                They will receive an email with an invitation to join.
              </Typography>
              <TextField
                autoFocus
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </>
          )}

          {inviteMode && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <FormControl>
                <FormLabel
                  id="invite-role-label"
                  sx={{
                    fontWeight: 600,
                    color: '#1C3045',
                    '&.Mui-focused': { color: '#1C3045' },
                  }}
                >
                  Role for this user
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="invite-role-label"
                  value={role}
                  onChange={(e) =>
                    setRole(e.target.value === 'admin' ? 'admin' : 'user')
                  }
                  sx={{ mt: 0.5 }}
                >
                  <FormControlLabel
                    value="admin"
                    control={<Radio disableRipple />}
                    label="Admin"
                  />
                  <FormControlLabel
                    value="user"
                    control={<Radio disableRipple />}
                    label="User"
                  />
                </RadioGroup>
              </FormControl>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: role === 'admin' ? 'warning.light' : 'grey.300',
                  borderRadius: 1,
                  backgroundColor: role === 'admin' ? '#FFF8E1' : '#F7F7F7',
                }}
              >
                {role === 'admin' ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5, color: '#1C3045' }}
                    >
                      Admins can do everything you can, including:
                    </Typography>
                    <Typography
                      component="ul"
                      variant="body2"
                      sx={{ m: 0, pl: 3, color: '#1C3045' }}
                    >
                      <li>Manage billing and subscription</li>
                      <li>
                        Invite, remove, and change the role of other users in
                        your organisation
                      </li>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontStyle: 'italic', color: '#1C3045' }}
                    >
                      Only invite as admin if you trust this person to view or
                      make changes to these details on behalf of the
                      organisation.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5, color: '#1C3045' }}
                    >
                      Users can collaborate on funding work:
                    </Typography>
                    <Typography
                      component="ul"
                      variant="body2"
                      sx={{ m: 0, pl: 3, color: '#1C3045' }}
                    >
                      <li>Find matches and draft applications</li>
                      <li>Add, edit, and delete funding requests</li>
                      <li>Edit organisation profile and funding needs</li>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontStyle: 'italic', color: '#1C3045' }}
                    >
                      They cannot manage billing, invite/remove users in your
                      organisation, or change other users roles within your
                      organisation.
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}

          {error && (
            <Alert ref={alertRef} severity="error" sx={{ mb: 2 }} tabIndex={-1}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert
              ref={alertRef}
              severity="success"
              sx={{ mb: 2, mt: 1 }}
              tabIndex={-1}
            >
              {successMessage}
            </Alert>
          )}

          {invitationLink && inviteMode === 'link' && (
            <Box
              sx={{
                mt: 2,
                p: 1,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f5f5f5',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-all',
                  mx: 1,
                  flex: 1,
                }}
              >
                {invitationLink}
              </Typography>
              <Tooltip
                title={copied ? 'Copied!' : 'Copy link'}
                placement="left"
              >
                <IconButton
                  onClick={handleCopyLink}
                  disableRipple
                  size="small"
                  sx={{
                    color: copied ? '#4caf50' : '#1C3045',
                    '&:hover': {
                      color: 'primary.main',
                    },
                    '&:focus': {
                      backgroundColor: '#e8e8e8',
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disableRipple
            onClick={() => setInvitingUser(false)}
            sx={{
              mr: 1,
              textTransform: 'none',
              color: '#1C3045',
              fontSize: '16px',
              borderColor: '#1C3045',
              border: 2,
              borderRadius: 2,
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            {invitationLink ? 'Close' : 'Cancel'}
          </Button>
          {inviteMode && (
            <Button
              type="submit"
              variant="contained"
              disableRipple
              disabled={
                isLoading ||
                inviteMode === null ||
                (inviteMode === 'email' && !email)
              }
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
              sx={{
                borderRadius: 2,
                bgcolor: '#1C3045',
                color: 'white',
                textTransform: 'none',
                fontSize: '16px',
                boxShadow: 'none',
                width: '230px',
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              {isLoading && inviteMode === 'link'
                ? 'Creating Link...'
                : isLoading && inviteMode === 'email'
                  ? 'Sending Invitation...'
                  : inviteMode === 'link' && invitationLink
                    ? 'Create Another Link'
                    : inviteMode === 'link'
                      ? 'Create Link'
                      : inviteMode === 'email' && invitationLink
                        ? 'Send Another Invitation'
                        : 'Send Invitation'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}
